import React from "react";
import {
  Controller,
  UseFormReturn,
  useFieldArray,
  useWatch,
} from "react-hook-form";
import {
  AssemblyComponent,
  CostingItem,
  Operation,
  PredefinedOp,
  PredefinedQt,
  PredefinedRt,
  Tool,
} from "../../interfaces/app_types";
import { SectionHeader, RemoveBtn } from "./CostingField";
import { setValueAsNumber } from "../../utils/funcs/common";
import FormField from "../common/FormField";
import CustomModal from "../CustomModal";
import CostingBulkModalBody from "./CostingBulkModalBody";

interface CostingFormProps {
  form: UseFormReturn<CostingItem>;
  stagedItems: CostingItem[];
  predefinedOps: PredefinedOp[];
  predefinedQts: PredefinedQt[];
  predefinedRts: PredefinedRt[];
  opsLoading: boolean;
  opsError: boolean;
  qtLoading: boolean;
  qtError: boolean;
  rtLoading: boolean;
  rtError: boolean;
  onStage: (data: CostingItem) => void;
  onBulkStage: (data: CostingItem[]) => void;
}

const CostingForm: React.FC<CostingFormProps> = ({
  form,
  stagedItems,
  predefinedOps,
  predefinedQts,
  predefinedRts,
  opsLoading,
  qtLoading,
  rtLoading,
  opsError,
  qtError,
  rtError,
  onStage,
  onBulkStage,
}) => {
  const TOOL_PRESETS = [
    "BLANKING & HOLE PIERCING COMPOUND TOOL",
    "BENDING TOOL",
    "EMBOSSING TOOL",
    "U BENDING TOOL",
    "BLANKING & HOLE PIERCING TOOL",
    "CUTTING & BENDING PROGRESSIVE TOOL",
    "WLEDING FIXTURE",
  ];
  const {
    register,
    setValue,
    handleSubmit,
    control,
    formState: { errors },
  } = form;

  const {
    fields: lineFields,
    append: appendLineItems,
    remove: removeLineItems,
  } = useFieldArray({ control, name: "lineItems" });

  const {
    fields: toolFields,
    append: appendTool,
    remove: removeTool,
  } = useFieldArray({ control, name: "tools" });

  const laserRate = useWatch({ control, name: "laser_rate" });
  const watchedLineItems = useWatch({ control, name: "lineItems" });

  const addPredefinedOp = () =>
    appendLineItems({
      type: "op",
      id: predefinedOps[0]?.id ?? "",
      count: 1,
      custom: false,
    });

  const addCustomOp = () =>
    appendLineItems({
      type: "custom_op",
      custom: true,
      title: "",
      count: 1,
      rate: 0,
      unit: "per_piece",
    });

  const addAssemblyComponent = () =>
    appendLineItems({ type: "assembly", drawing_no: "", quantity: 1 });

  const handleStage = (data: CostingItem) => {
    const { lineItems = [], ...rest } = data;

    onStage({
      ...rest,
      lineItems,
      operations: lineItems
        .filter((i) => i.type === "op" || i.type === "custom_op")
        .map(({ type, ...op }) => op as Operation),
      assembly: lineItems
        .filter((i) => i.type === "assembly")
        .map(({ type, ...a }) => a as AssemblyComponent),
    });
  };

  return (
    <form onSubmit={handleSubmit(handleStage)} className="costing-panel">
      <div className="d-flex justify-content-between">
        <h3>Part Geometry &amp; Core Attributes</h3>
        <div
          className="styled-btn--std mobile-d-none"
          data-bs-toggle="modal"
          data-bs-target="#bulkCostingModal"
        >
          Add Bulk +
        </div>
      </div>
      <div className="costing-row-2">
        <FormField
          title="PART NAME"
          formRegister={register("part_name")}
          errorMessage={errors.part_name?.message}
          fullWidth={true}
        />
        <FormField
          title="DRAWING NO."
          formRegister={register("drawing_no")}
          errorMessage={errors.drawing_no?.message}
          fullWidth={true}
        />
      </div>

      <div className="costing-row-4">
        <FormField
          title="Length (mm)"
          fieldType="number"
          formRegister={register("length")}
          errorMessage={errors.length?.message}
          fullWidth={true}
        />
        <FormField
          title="Breadth (mm)"
          fieldType="number"
          formRegister={register("breadth")}
          errorMessage={errors.breadth?.message}
          fullWidth={true}
        />
        <FormField
          title="Thickness (mm)"
          fieldType="number"
          step={0.01}
          formRegister={register("thickness")}
          errorMessage={errors.thickness?.message}
          fullWidth={true}
        />

        <div>
          {qtLoading && <p className="costing-hint">Loading quantity...</p>}
          {qtError && (
            <p className="costing-hint costing-hint--error">
              Failed to load quantity.
            </p>
          )}
          <div className="d-flex align-items-center gap-2">
            <label htmlFor="costing-quantity">Quantity</label>
            <select
              id="costing-quantity"
              className="costing-input form-select"
              {...register(`quantity`, { valueAsNumber: true })}
            >
              {predefinedQts.map((field) => (
                <option key={field.id} value={field.value}>
                  {field.value}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      <FormField
        title="Laser Rate (opt.)"
        fieldType="number"
        step={0.01}
        formRegister={register("laser_rate")}
        errorMessage={errors.laser_rate?.message}
        fullWidth={true}
      />
      {!laserRate && (
        <div className="col-12">
          {rtLoading && <p className="costing-hint">Loading rates...</p>}
          {rtError && (
            <p className="costing-hint costing-hint--error">
              Failed to load rates.
            </p>
          )}
          <div className="pe-3 p-1 d-flex gap-5">
            <label htmlFor="costing-rmrate">Rate</label>

            <div className="input-group">
              <input
                id="costing-rmrate"
                type="number"
                className="form-control costing-input--number"
                placeholder="Enter or select rate"
                {...register(`raw_material_rate`, {
                  valueAsNumber: true,
                })}
              />
              <button
                type="button"
                className="btn btn-outline-secondary dropdown-toggle"
                data-bs-toggle="dropdown"
              ></button>
              <ul className="dropdown-menu dropdown-menu-end">
                {predefinedRts.map((field) => (
                  <li key={field.id}>
                    <button
                      type="button"
                      className="dropdown-item"
                      onClick={() =>
                        setValue(`raw_material_rate`, Number(field.value))
                      }
                    >
                      {field.value}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
      <Controller
        name="is_pipe"
        control={control}
        render={({ field: { value } }) => (
          <FormField
            title="Is Pipe?"
            value={value}
            fieldType="checkbox"
            inputId="is_pipe"
            formRegister={{ ...register("is_pipe") }}
          />
        )}
      />
      <hr className="costing-divider" />

      {/* Operations */}
      <SectionHeader title="Operations">
        <button
          type="button"
          className="costing-sm-btn"
          onClick={addPredefinedOp}
          disabled={opsLoading || opsError}
        >
          + Operation
        </button>
        <button type="button" className="costing-sm-btn" onClick={addCustomOp}>
          + Custom Operation
        </button>
        <button
          type="button"
          className="costing-sm-btn"
          onClick={addAssemblyComponent}
        >
          + Assembly Component
        </button>
      </SectionHeader>
      {opsLoading && <p className="costing-hint">Loading operations…</p>}
      {opsError && (
        <p className="costing-hint costing-hint--error">
          Failed to load operations.
        </p>
      )}

      {lineFields.map((field, idx) => {
        const item = watchedLineItems?.[idx];

        if (item?.type === "assembly") {
          return (
            <div key={field.id} className="costing-op-row">
              <select
                className="costing-input costing-input--grow"
                {...register(`lineItems.${idx}.drawing_no`)}
              >
                <option value="">Select component…</option>
                {stagedItems.map((s) => (
                  <option key={s.drawing_no} value={s.drawing_no}>
                    {s.drawing_no}
                    {s.part_name ? ` — ${s.part_name}` : ""}
                  </option>
                ))}
              </select>
              <input
                type="number"
                placeholder="Qty"
                defaultValue={1}
                title="Quantity"
                className="costing-input costing-input--count"
                {...register(`lineItems.${idx}.quantity`, {
                  setValueAs: setValueAsNumber,
                })}
              />
              <RemoveBtn onClick={() => removeLineItems(idx)} />
            </div>
          );
        }

        // op or custom_op
        const isCustom = item?.type === "custom_op";
        return (
          <div key={field.id} className="costing-op-row">
            {isCustom ? (
              <>
                <span className="costing-badge">Custom</span>
                <input
                  placeholder="Operation Title"
                  className="costing-input costing-input--grow"
                  {...register(`lineItems.${idx}.title`)}
                />
                <input
                  type="number"
                  placeholder="Rate"
                  className="costing-input costing-input--rate"
                  {...register(`lineItems.${idx}.rate`, {
                    setValueAs: setValueAsNumber,
                  })}
                />
                <select
                  className="costing-input"
                  {...register(`lineItems.${idx}.unit`)}
                >
                  <option value="per_piece">per piece</option>
                  <option value="per_weight">per kg</option>
                  <option value="fixed">fixed</option>
                </select>
              </>
            ) : (
              <select
                className="costing-input costing-input--grow"
                {...register(`lineItems.${idx}.id`)}
              >
                {predefinedOps.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.title}
                  </option>
                ))}
              </select>
            )}
            <input
              type="number"
              placeholder="Count"
              title="Stroke / Weld Count"
              className="costing-input costing-input--count"
              {...register(`lineItems.${idx}.count`, {
                setValueAs: setValueAsNumber,
              })}
            />
            <RemoveBtn onClick={() => removeLineItems(idx)} />
          </div>
        );
      })}

      <hr className="costing-divider" />

      {/* Tooling */}
      <SectionHeader title="Tooling Costs">
        <button
          type="button"
          className="costing-sm-btn"
          onClick={() =>
            appendTool({ title: "", rate: "", unit: "per_piece" } as Tool)
          }
        >
          + Tooling
        </button>
      </SectionHeader>

      <datalist id="tool-presets">
        {TOOL_PRESETS.map((t) => (
          <option key={t} value={t} />
        ))}
      </datalist>

      {toolFields.map((field, idx) => (
        <div key={field.id} className="costing-op-row">
          <input
            list="tool-presets"
            placeholder="Select or type tool name…"
            className="costing-input costing-input--grow"
            {...register(`tools.${idx}.title`)}
          />
          <input
            placeholder="Rate / Ref Part"
            className="costing-input costing-input--grow"
            {...register(`tools.${idx}.rate`)}
          />
          <RemoveBtn onClick={() => removeTool(idx)} />
        </div>
      ))}

      <hr className="costing-divider" />

      <button type="submit" className="costing-primary-btn">
        Stage Component
      </button>

      <CustomModal
        modalId="bulkCostingModal"
        modalTitle="Add Bulk Costing"
        modalBody={<CostingBulkModalBody onBulkStage={onBulkStage} />}
      />
    </form>
  );
};

export default CostingForm;
